import { sortBy } from 'lodash';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Modules, useLoggedInUserPermissions } from '~/components/Auth';
import { WithPerspectives } from '~/components/MyWorkspace/ManagePerspectives';
import { TableWidgetComponentFactory } from '~/components/MyWorkspace/TableWidget';
import { RewardingTabs } from '~/data/tabs';
import { useWorkspaceSettings } from '~/scenes/MyWorkspace/hooks';
import { People } from '~/services/Http';
import { useGetTeamPerformanceScores } from '~/services/Http/hooks';

import { WidgetProps } from '../../data/widgets';

import { TableHeader } from './components/TableHeader';
import { TeamMemberPerformance as TeamPerformance } from './components/TeamMemberPerformance';

export interface TeamPerformanceWidgetProps
  extends WidgetProps<WithPerspectives> {}

const TeamPerformanceWidgetComponent = TableWidgetComponentFactory({
  TableRowComponent: TeamPerformance,
  getIdentifier: (item: People.PerformanceGoalDto) => `${item.id}`,
  TableHeaderComponent: TableHeader,
  dataCy: 'team-performance-widget',
  allTableDialogProps: {
    maxWidth: 'lg',
    fullWidth: true,
  },
});

export const TeamPerformanceWidget: React.FC<TeamPerformanceWidgetProps> = ({
  title,
}) => {
  // TODO activeWorkspaceRole should be passed through as part of perspective
  const { activeWorkspaceRole } = useWorkspaceSettings();

  const { t } = useTranslation();

  const {
    data: performanceScores,
    loading,
    failedToLoad,
  } = useGetTeamPerformanceScores({
    employeeId: 'me',
    roles: [activeWorkspaceRole],
  });

  const performance = useMemo(() => {
    // QUICKFIX: BE shouldn't return performances without employee data
    const performancesWithEmployee = (performanceScores ?? []).filter(
      (p) => !!p?.employee,
    );

    return sortBy(
      performancesWithEmployee,
      (performance) => performance.employee.lastName,
    );
  }, [performanceScores]);

  const permissions = useLoggedInUserPermissions();
  const rewardingModuleEnabledTabs = permissions.enabledTabs[Modules.REWARDING];

  return (
    <TeamPerformanceWidgetComponent
      title={title || t('teamPerformance')}
      data={performance}
      loading={loading}
      failedToLoad={failedToLoad}
      noDataMessage={t('noPerformance')}
      isTabDisabled={
        !!rewardingModuleEnabledTabs &&
        !rewardingModuleEnabledTabs.includes(RewardingTabs.GENERAL)
      }
    />
  );
};
